import {objectFromEntries} from '@benchling/js-utils';
import produce from 'immer';
import Immutable from 'immutable';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {InaccessibleInfo} from 'auth/RequestAccess';
import {EventConstants} from 'constants/EventConstants';
import {LegacyMeasurement} from 'constants/MeasurementConstants';
import ModelTypeConstants, {StorableItemType} from 'constants/ModelTypeConstants';
import {SavingState} from 'constants/RegistryConstants';
import {
  Batch,
  Container,
  ContainerContent,
  ContainerSummary,
  ContainerTransferInfo,
  Grid,
  GridSummary,
  HydratedContainerTransferInfo,
  InaccessibleStorable,
  LabelTemplate,
  ListedLocation,
  Location,
  LocationAncestry,
  LocationSummary,
  PlateSummaryExtras,
  Printer,
  Storable,
  StorableSummary,
  StorableSummaryExtras,
  isInaccessibleStorable,
} from 'constants/SampleConstants';
import {CheckOutStatusInfo} from 'constants/SampleStatusConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import {
  Listing,
  listingComplete,
  listingFail,
  listingMarkStale,
  listingRestart,
  listingStart,
} from 'lib/Listings';
import Store from 'lib/Store';
import {loadingInvalidated} from 'models/LoadingStateReduxActions';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {forceFetchManagedModel} from 'models/ManagedModelUtil';
import {MODEL_TYPE} from 'models/managers/StorableSummaryManager';
import {getStore} from 'redux-stores/Store';
import * as SampleUtil from 'samples/SampleUtil';
import {isFixedPlate} from 'samples/SampleUtil';
import * as StorableAuth from 'samples/storables/StorableAuth';
import APIUtil from 'util/APIUtil';
import {Optional} from 'util/Types';

type StorableAuthState = StorableAuth.StorableAuthState;

interface BulkDraft {
  batches: {[batchId: string]: Batch};
  grids: {[gridId: string]: Grid | null};
  gridSummaries: {[gridId: string]: GridSummary | null};
  containers: {[containerId: string]: Container | null};
  containerSummaries: {[containerId: string]: ContainerSummary | null};
  locations: {[locationId: string]: Location | null};
  locationSummaries: {[locationId: string]: LocationSummary | null};
  plateSummaryExtras: PlateSummaryExtras;
  storableSummaryExtras: StorableSummaryExtras;
  containableContainerListings: Immutable.Map<string, Listing<string, ContainableListingParam>>;
  containablePlateListings: Immutable.Map<string, Listing<string, ContainableListingParam>>;
}

export interface GridWithLoading extends Grid {
  isLoading?: boolean;
}

/**
 * Helper for handling actions based on a storable type
 * @param {string} storableType The type of storable (one of the ModelTypeConstants)
 * @param {function} gridCallback The function to be executed if storableType is a grid type
 * @param {function} containerCallback The function to be executed if storableType is a container
 * @param {function} locationCallback The function to be executed if storableType is a location
 * @param {*} defaultValue The return value if storable type is not a valid type
 * @returns The value returned from a storable callback or undefined if a callback does not
 *          explicitly return
 */
function handleStorableType<T>(
  storableType: string,
  gridCallback: () => T,
  containerCallback: () => T,
  locationCallback: () => T,
  defaultValue: T
): T {
  switch (storableType) {
    case ModelTypeConstants.BOX:
    case ModelTypeConstants.PLATE:
    case ModelTypeConstants.FIXED_PLATE:
    case ModelTypeConstants.MATRIX_PLATE: {
      return gridCallback();
    }
    case ModelTypeConstants.CONTAINER: {
      return containerCallback();
    }
    case ModelTypeConstants.LOCATION: {
      return locationCallback();
    }
    default:
      assertAndContinue(false, `Unexpected storableType: ${storableType}`);
  }
  return defaultValue;
}

export interface ContainableListingParam {
  containableId: string;
}

export type ContainableContainerListing = Listing<ContainerSummary, ContainableListingParam> & {
  storableSummaryExtras: StorableSummaryExtras;
};

export type ContainablePlateListing = Listing<GridSummary | null, ContainableListingParam> & {
  plateSummaryExtras: PlateSummaryExtras;
  storableSummaryExtras: StorableSummaryExtras;
};

class StorableStore extends Store {
  batches!: {[batchId: string]: Batch};

  containers!: {[containerId: string]: Container | null};
  containerSummaries!: {[containerId: string]: ContainerSummary | null};
  containerTransferInfos!: {[containerId: string]: ContainerTransferInfo};
  grids!: {[gridId: string]: Grid | null};
  gridSummaries!: {[gridId: string]: GridSummary | null};
  locations!: {[locationId: string]: Location | null};
  locationSummaries!: {[locationId: string]: LocationSummary | null};

  // Listings of container IDs, indexed by listing ID.
  containableContainerListings!: Immutable.Map<string, Listing<string, ContainableListingParam>>;
  // Listings of grid IDs, indexed by listing ID.
  containablePlateListings!: Immutable.Map<string, Listing<string, ContainableListingParam>>;
  labelTemplates!: Array<LabelTemplate> | null;
  listingIdToContainableId!: {[listingId: string]: string};
  printers!: Array<Printer> | null;
  plateSummaryExtras!: PlateSummaryExtras;
  storableSummaryExtras!: StorableSummaryExtras;
  savingState!: SavingState;
  storableAuthState!: StorableAuthState;

  _bulkUpdate(actOnBulkDraft: (bulkDraft: BulkDraft) => void): void {
    const startDraft: BulkDraft = {
      batches: this.batches,
      grids: this.grids,
      containers: this.containers,
      locations: this.locations,
      gridSummaries: this.gridSummaries,
      containerSummaries: this.containerSummaries,
      locationSummaries: this.locationSummaries,
      plateSummaryExtras: this.plateSummaryExtras,
      storableSummaryExtras: this.storableSummaryExtras,
      containableContainerListings: this.containableContainerListings,
      containablePlateListings: this.containablePlateListings,
    };

    const finalDraft = produce(startDraft, actOnBulkDraft);

    this.batches = finalDraft.batches;
    this.grids = finalDraft.grids;
    this.containers = finalDraft.containers;
    this.locations = finalDraft.locations;
    this.gridSummaries = finalDraft.gridSummaries;
    this.containerSummaries = finalDraft.containerSummaries;
    this.locationSummaries = finalDraft.locationSummaries;
    this.plateSummaryExtras = finalDraft.plateSummaryExtras;
    this.storableSummaryExtras = finalDraft.storableSummaryExtras;
    this.containableContainerListings = finalDraft.containableContainerListings;
    this.containablePlateListings = finalDraft.containablePlateListings;
  }

  resetState(): void {
    // Batches aren't Storables, but are stored here so as not to be conflated with the legacy
    // SampleStore.batchesByFile, which includes containers and information for processes.
    // New samples should read from batches from this store.
    // batches is a mapping from batch API identifier to batch to_dict, loaded with the arguments
    // specified by Batch.batches_to_dicts.
    this.batches = {};

    // The storable summary mappings should be kept in sync with the storable mappings
    // E.g. an update to container `con_1` in this.containers will also update this.containerSummaries
    this.containers = {};
    this.containerSummaries = {};
    this.containerTransferInfos = {};
    this.grids = {};
    this.gridSummaries = {};
    this.locations = {};
    this.locationSummaries = {};

    this.containableContainerListings = Immutable.Map<string, Listing<string, ContainableListingParam>>();
    this.containablePlateListings = Immutable.Map<string, Listing<string, ContainableListingParam>>();
    this.labelTemplates = null;
    this.listingIdToContainableId = {};
    this.printers = null;
    this.plateSummaryExtras = {viewableWellCountByPlateId: {}};
    this.storableSummaryExtras = {};
    this.savingState = SavingState.COMPLETED;
    this.storableAuthState = StorableAuth.SubStore.getInitialState();
  }

  getSavingState(): SavingState {
    return this.savingState;
  }

  setSavingState(savingState: SavingState): void {
    this.savingState = savingState;
  }

  __serverBootstrap({batches}: {batches?: Array<Batch>} = {}): void {
    if (batches) {
      this._bulkUpdate((bulkDraft) => {
        this._updateBatches(batches, bulkDraft);
      });
    }
  }

  getAuthErrorMessage(storableId: string): string {
    return StorableAuth.SubStore.getErrorMessage(this.storableAuthState, storableId);
  }

  getInaccessibleInfo(storableId: string): InaccessibleInfo | null {
    return StorableAuth.SubStore.getInaccessibleInfo(this.storableAuthState, storableId);
  }

  getBatches(): {[batchId: string]: Batch} {
    return this.batches;
  }

  getBatch(batchId: string): Batch | null {
    return this.batches[batchId] || null;
  }

  getContainerListingItemsFromIds(items: Array<string>): Array<ContainerSummary> | null {
    // Because of invalidation (invalidateStorable), some container summaries are set to null.
    // We don't want to return a listing where any items are null, so we set listing.items itself to
    // null in that case.
    const containerSummaries = [];
    for (const containerId of items) {
      const containerSummary = this.getContainerSummary(containerId);
      if (!containerSummary) {
        return null;
      }
      containerSummaries.push(containerSummary);
    }
    return containerSummaries;
  }

  getContainableContainerListings(): {
    [listingId: string]: ContainableContainerListing;
  } {
    return this.containableContainerListings
      .map((listing) => {
        const items = listing.items ? this.getContainerListingItemsFromIds(listing.items) : null;
        return {
          ...listing,
          items,
          storableSummaryExtras: this.getStorableSummaryExtras(),
        };
      })
      .toObject();
  }

  getContainablePlateListings(): {
    [listingId: string]: ContainablePlateListing;
  } {
    return this.containablePlateListings
      .map((listing) => {
        const items = listing.items ? listing.items.map((plateId) => this.getGridSummary(plateId)) : null;
        return {
          ...listing,
          items,
          plateSummaryExtras: this.getPlateSummaryExtras(),
          storableSummaryExtras: this.getStorableSummaryExtras(),
        };
      })
      .toObject();
  }

  getListingIdsByContainableId(containableId: string): Array<string> {
    return Object.entries(this.listingIdToContainableId)
      .filter(([listingId, listingContainableId]) => listingContainableId === containableId)
      .map(([listingId, listingContainableId]) => listingId);
  }

  getContainers(): {[containerId: string]: Container | null} {
    return this.containers;
  }

  getContainerSummaries(): {[containerId: string]: ContainerSummary} {
    const ids = [...Object.keys(this.containers), ...Object.keys(this.containerSummaries)];
    return _.object(ids.map((id) => [id, this.getContainerSummary(id)]));
  }

  getContainer(containerId: string): Container | null {
    return this.containers[containerId] || null;
  }

  getContainerSummary(containerId: string): ContainerSummary | null {
    return this.containers[containerId] || this.containerSummaries[containerId] || null;
  }

  getContainerTransferInfo(containerId: string): HydratedContainerTransferInfo | null {
    const containerTransferInfo = this.containerTransferInfos[containerId];
    if (!containerTransferInfo) {
      return null;
    }
    const inputStorables = containerTransferInfo.inputStorableIds.map((id) => this.getStorableSummary(id));
    const outputStorables = containerTransferInfo.outputStorableIds.map((id) => this.getStorableSummary(id));
    const anyStorableUnloaded =
      inputStorables.some((storable) => !storable) || outputStorables.some((storable) => !storable);
    if (anyStorableUnloaded) {
      return null;
    }
    return {inputStorables, outputStorables};
  }

  /**
   * Return a grid, denormalizing the grid cell containers from `this.containers` to `grid.cells`.
   *
   * Grid cell containers are stored in `this.containers` upon processing to ensure that they are updated
   * upon writes and the most up-to-date container is returned.
   *
   * Generic type used to guarantee that given a FullyAccessibleGrid, a FullyAccessibleGrid will be returned
   * rather than loosening the type to just Grid (can have inaccessible containers).
   */
  getGrid<T extends Grid>(gridId: string): (T & {isLoading?: boolean}) | null {
    const grid = this.grids[gridId];
    if (!grid) {
      return null;
    }
    // Grid may require fetch if not yet in the store or any child containers are invalidated.
    let requiresFetch = false;
    const cells = grid.cells.map((rowCells) =>
      rowCells.map((cell) => {
        if (!cell) {
          return null;
        }
        const container = !isInaccessibleStorable(cell.container)
          ? this.containers[cell.container.id]
          : cell.container;
        if (!container) {
          requiresFetch = true;
          return null;
        }
        return {...cell, container};
      })
    );
    if (requiresFetch) {
      return null;
    }
    return {...grid, cells} as T & {isLoading?: boolean};
  }

  getGrids(): {[gridId: string]: GridWithLoading | null} {
    return this.grids;
  }

  getGridSummary(gridId: string): GridSummary | null {
    return this.grids[gridId] || this.gridSummaries[gridId] || null;
  }

  getLabelTemplate(labelTemplateId: string): Optional<LabelTemplate> {
    if (!this.labelTemplates) {
      return null;
    }
    return this.labelTemplates.find(({id}) => id === labelTemplateId);
  }

  getLabelTemplates(registryId: string): Array<LabelTemplate> | null {
    if (!this.labelTemplates) {
      return null;
    }
    return this.labelTemplates.filter((labelTemplate) => labelTemplate.registryId === registryId);
  }

  getLocation(locationId: string): Location | null {
    return this.locations[locationId] || null;
  }

  getLocationSummary(locationId: string): LocationSummary | null {
    return this.locations[locationId] || this.locationSummaries[locationId] || null;
  }

  getPrinter(printerId: string): Optional<Printer> {
    if (!this.printers) {
      return null;
    }
    return this.printers.find(({id}) => id === printerId);
  }

  getPrinters(registryId: string): Array<Printer> | null {
    if (!this.printers) {
      return null;
    }
    return this.printers.filter((printer) => printer.registryId === registryId);
  }

  _getLocationAncestry(locationId: Optional<string>): LocationAncestry | null {
    if (!locationId) {
      return null;
    }
    const result = this.getLocationSummary(locationId);
    if (!result) {
      // Location has not yet loaded
      return null;
    }
    return {...result, parent: this._getLocationAncestry(result.parentStorableId)};
  }

  getListedLocation(locationId: string): ListedLocation | null {
    const result = this.getLocation(locationId);
    if (!result) {
      // Location has not yet loaded
      return null;
    }
    return {...result, parent: this._getLocationAncestry(result.parentStorableId)};
  }

  _isStorableOfType(storable: StorableSummary, storableType: StorableItemType): boolean {
    switch (storableType) {
      case ModelTypeConstants.BOX:
        return APIUtil.isBox(storable.id);
      case ModelTypeConstants.PLATE:
        return APIUtil.isPlate(storable.id);
      case ModelTypeConstants.FIXED_PLATE:
        return isFixedPlate(storable);
      case ModelTypeConstants.MATRIX_PLATE:
        return APIUtil.isPlate(storable.id) && !isFixedPlate(storable);
      case ModelTypeConstants.CONTAINER:
        return APIUtil.isContainer(storable.id);
      case ModelTypeConstants.LOCATION:
        return APIUtil.isLocation(storable.id);
      default:
        assertAndContinue(false, `Unexpected storableType: ${storableType}`);
        return false;
    }
  }

  _getStorablesIncludingType(storableType: string): {[storableId: string]: Storable | null} {
    return handleStorableType<{[storableId: string]: Storable | null}>(
      storableType,
      () => this.grids,
      () => this.containers,
      () => this.locations,
      {}
    );
  }

  getStorables(registryId: string, storableTypes: Array<StorableItemType>): {[storableId: string]: Storable} {
    return objectFromEntries(
      storableTypes
        .map((storableType) =>
          Object.values(this._getStorablesIncludingType(storableType)).filter(
            (storable): storable is Storable =>
              storable != null &&
              storable.storableSchema.registryId === registryId &&
              this._isStorableOfType(storable, storableType)
          )
        )
        .flat()
        .map((storable) => [storable.id, storable])
    );
  }

  getStorable(storableId: string): Storable | null {
    const storables = this._getStorablesIncludingType(APIUtil.apiIdToType(storableId));
    return storables[storableId] || null;
  }

  _getStorableSummariesIncludingType(storableType: StorableItemType): {
    [storableId: string]: StorableSummary | null;
  } {
    return handleStorableType<{[storableId: string]: StorableSummary | null}>(
      storableType,
      () => this.gridSummaries,
      () => this.containerSummaries,
      () => this.locationSummaries,
      {}
    );
  }

  getPlateSummaryExtras(): PlateSummaryExtras {
    return this.plateSummaryExtras;
  }

  getStorableSummaryExtras(): StorableSummaryExtras {
    return this.storableSummaryExtras;
  }

  getStorableSummaries(
    registryId: string,
    storableTypes: Array<StorableItemType>
  ): {[storableId: string]: StorableSummary} {
    return objectFromEntries(
      storableTypes
        .map((storableType) =>
          Object.values(this._getStorableSummariesIncludingType(storableType)).filter(
            (storable): storable is StorableSummary =>
              storable != null &&
              storable.storableSchema.registryId === registryId &&
              this._isStorableOfType(storable, storableType)
          )
        )
        .flat()
        .map((storable) => [storable.id, storable])
    );
  }

  getStorableSummary(storableId: string): StorableSummary | null {
    if (!storableId) {
      return null;
    }
    return handleStorableType<StorableSummary | null>(
      APIUtil.apiIdToType(storableId),
      () => this.getGridSummary(storableId),
      () => this.getContainerSummary(storableId),
      () => this.getLocationSummary(storableId),
      null
    );
  }

  _setStorableAndStorableSummary(storableId: string, storable: Storable | null, bulkDraft: BulkDraft): void {
    handleStorableType(
      APIUtil.apiIdToType(storableId),
      () => {
        bulkDraft.grids[storableId] = storable as Grid | null;
        bulkDraft.gridSummaries[storableId] = storable as Grid | null;
      },
      () => {
        bulkDraft.containers[storableId] = storable as Container | null;
        bulkDraft.containerSummaries[storableId] = storable as ContainerSummary | null;
      },
      () => {
        bulkDraft.locations[storableId] = storable as Location | null;
        bulkDraft.locationSummaries[storableId] = storable as ContainerSummary | null;
      },
      null
    );
  }

  _updateBatches(newBatches: Array<Batch>, bulkDraft: BulkDraft): void {
    for (const newBatch of newBatches) {
      bulkDraft.batches[newBatch.id] = newBatch;
    }
  }

  _mergePlateSummaryExtras(plateSummaryExtras: PlateSummaryExtras, bulkDraft: BulkDraft): void {
    Object.entries(plateSummaryExtras).forEach(([_key, newValue]) => {
      const key = _key as keyof PlateSummaryExtras;
      bulkDraft.plateSummaryExtras[key] = {
        ...bulkDraft.plateSummaryExtras[key],
        ...newValue,
      };
    });
  }

  _mergeStorableSummaryExtras(storableSummaryExtras: StorableSummaryExtras, bulkDraft: BulkDraft): void {
    Object.entries(storableSummaryExtras).forEach(([key, newValue]) => {
      const id = key as keyof StorableSummaryExtras;
      bulkDraft.storableSummaryExtras[id] = {
        ...bulkDraft.storableSummaryExtras[id],
        ...newValue,
      };
    });
  }

  _processStorableSummary(storable: StorableSummary, bulkDraft: BulkDraft): void {
    handleStorableType(
      APIUtil.apiIdToType(storable.id),
      () => {
        bulkDraft.gridSummaries[storable.id] = storable as GridSummary;
      },
      () => {
        bulkDraft.containerSummaries[storable.id] = storable as ContainerSummary;
      },
      () => {
        bulkDraft.locationSummaries[storable.id] = storable as LocationSummary;
      },
      null
    );
  }

  _processParentStorableSummary(bulkDraft: BulkDraft, parentStorable?: StorableSummary): void {
    if (parentStorable) {
      this._processStorableSummary(parentStorable, bulkDraft);
    }
  }

  _processContainer(
    bulkDraft: BulkDraft,
    container: Container | InaccessibleStorable,
    parentStorable?: StorableSummary,
    batches?: Array<Batch>
  ): void {
    if (!isInaccessibleStorable(container)) {
      this._setStorableAndStorableSummary(container.id, container, bulkDraft);
    }

    this._processParentStorableSummary(bulkDraft, parentStorable);
    if (batches) {
      this._updateBatches(batches, bulkDraft);
    }
  }

  _processGridSummary(gridSummary: GridSummary, bulkDraft: BulkDraft): void {
    const {id} = gridSummary;
    bulkDraft.grids[id] = {
      ...bulkDraft.grids[id],
      ...(gridSummary as Grid),
      isLoading: false,
    } as GridWithLoading;
    bulkDraft.gridSummaries[id] = {...bulkDraft.gridSummaries[id], ...gridSummary};
  }

  _processGrid(grid: Grid, bulkDraft: BulkDraft): void {
    const newGrid: GridWithLoading = {...grid, isLoading: false};
    bulkDraft.grids[grid.id] = newGrid;
    bulkDraft.gridSummaries[grid.id] = newGrid;

    // Normalize the grid cell containers to store in `this.container`; data is denormalized in `getGrid`.
    grid.cells.forEach((rowCells) => {
      rowCells.forEach((cell) => {
        if (!cell) {
          return;
        }
        this._processContainer(bulkDraft, cell.container);
      });
    });
  }

  _isInvalidatableStorable(storableId: string): boolean {
    // Location invalidation not yet supported, so keep stale for now
    return APIUtil.isStorable(storableId) && APIUtil.apiIdToType(storableId) !== ModelTypeConstants.LOCATION;
  }

  _invalidateStorables(storableIds: Array<string>, bulkDraft: BulkDraft): void {
    const invalidatableStorableIds = storableIds.filter((id) => this._isInvalidatableStorable(id));
    invalidatableStorableIds.forEach((storableId) => {
      this._setStorableAndStorableSummary(storableId, null, bulkDraft);
    });
    // Clear redux loading state for the invalidated storables
    getStore().dispatch(loadingInvalidated(ManagedModelType.STORABLE, invalidatableStorableIds));
    getStore().dispatch(loadingInvalidated(ManagedModelType.STORABLE_SUMMARY, invalidatableStorableIds));
    getStore().dispatch(loadingInvalidated(ManagedModelType.CONTAINER, invalidatableStorableIds));
    getStore().dispatch(loadingInvalidated(MODEL_TYPE, invalidatableStorableIds));
  }

  async _invalidateContainedSample(containableId: string): Promise<void> {
    const module = await import('ui-library/chips/BioentityChip');
    if (APIUtil.isBiologicalEntity(containableId)) {
      await module.doBioentityChipQuery(containableId);
    } else if (APIUtil.isBatch(containableId)) {
      forceFetchManagedModel(ManagedModelType.BATCH, containableId);
    }
  }

  _invalidateContainedItemListings(containedItemIds: Array<string>, bulkDraft: BulkDraft): void {
    const containedItemIdSet = new Set(containedItemIds);

    for (const [listingId, listing] of bulkDraft.containableContainerListings) {
      if (containedItemIdSet.has(listing.param.containableId)) {
        bulkDraft.containableContainerListings = bulkDraft.containableContainerListings.set(
          listingId,
          listingMarkStale(listing)
        );
      }
    }
    for (const [listingId, listing] of bulkDraft.containablePlateListings) {
      if (containedItemIdSet.has(listing.param.containableId)) {
        bulkDraft.containablePlateListings = bulkDraft.containablePlateListings.set(
          listingId,
          listingMarkStale(listing)
        );
      }
    }
  }

  _processStorableAndStorableSummary(storable: Storable, bulkDraft: BulkDraft): void {
    if (SampleUtil.isGrid(storable.id)) {
      this._processGrid(storable as Grid, bulkDraft);
    } else if (APIUtil.apiIdToType(storable.id) === ModelTypeConstants.CONTAINER) {
      // This is here for back-compatibility; augmenting the to_dict with isLoading is tech debt.
      const storableWithLoading = {...storable, isLoading: false};
      this._setStorableAndStorableSummary(storable.id, storableWithLoading, bulkDraft);
    } else {
      this._setStorableAndStorableSummary(storable.id, storable, bulkDraft);
    }
  }

  payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.STORABLES_UPDATED: {
        const {storableIds, updates} = action;
        this._bulkUpdate((bulkDraft) => {
          storableIds.forEach((storableId: string) => {
            handleStorableType(
              APIUtil.apiIdToType(storableId),
              () => {
                if (bulkDraft.grids[storableId]) {
                  bulkDraft.grids[storableId] = {
                    ...bulkDraft.grids[storableId],
                    ...updates,
                  };
                }
                if (bulkDraft.gridSummaries[storableId]) {
                  bulkDraft.gridSummaries[storableId] = {
                    ...bulkDraft.gridSummaries[storableId],
                    ...updates,
                  };
                }
              },
              () => {
                if (bulkDraft.containers[storableId]) {
                  bulkDraft.containers[storableId] = {
                    ...bulkDraft.containers[storableId],
                    ...updates,
                  };
                }
                if (bulkDraft.containerSummaries[storableId]) {
                  bulkDraft.containerSummaries[storableId] = {
                    ...bulkDraft.containerSummaries[storableId],
                    ...updates,
                  };
                }
              },
              () => {
                if (bulkDraft.locations[storableId]) {
                  bulkDraft.locations[storableId] = {
                    ...bulkDraft.locations[storableId],
                    ...updates,
                  };
                }
                if (bulkDraft.locationSummaries[storableId]) {
                  bulkDraft.locationSummaries[storableId] = {
                    ...bulkDraft.locationSummaries[storableId],
                    ...updates,
                  };
                }
              },
              null
            );
          });
        });
        this.emitChange();
        break;
      }

      case EventConstants.STORABLES_BULK_FETCH_COMPLETED: {
        const {storables} = action;
        this._bulkUpdate((bulkDraft) => {
          storables.forEach((storable: Storable) => {
            this._processStorableAndStorableSummary(storable, bulkDraft);
          });
        });
        this.emitChange();
        break;
      }

      case EventConstants.STORABLE_SUMMARIES_FETCH_COMPLETED: {
        const {storables, storableSummaryExtras} = action;
        this._bulkUpdate((bulkDraft) => {
          storables.forEach((storable: Storable) => {
            this._processStorableSummary(storable, bulkDraft);
          });
          if (storableSummaryExtras) {
            this._mergeStorableSummaryExtras(storableSummaryExtras, bulkDraft);
          }
        });
        this.emitChange();
        break;
      }

      case EventConstants.CONTAINER_FETCH_COMPLETED: {
        const {batches, container, parentStorable} = action;
        this._bulkUpdate((bulkDraft) => {
          const newContainer = {...container, isLoading: false};
          this._processContainer(bulkDraft, newContainer, parentStorable, batches);
        });
        this.emitChange();
        break;
      }

      case EventConstants.CONTAINER_BULK_FETCH_COMPLETED: {
        const {containersById, batchesById} = action;
        this._bulkUpdate((bulkDraft) => {
          for (const container of Object.values(containersById)) {
            this._processContainer(bulkDraft, container as Container);
          }
          if (batchesById) {
            this._updateBatches(Object.values(batchesById), bulkDraft);
          }
        });
        this.emitChange();
        break;
      }

      case EventConstants.CONTAINER_FETCH_TRANSFER_INFO_COMPLETED: {
        const {containerId, containerTransferInfo} = action;
        this.containerTransferInfos = {...this.containerTransferInfos, [containerId]: containerTransferInfo};
        this.emitChange();
        break;
      }

      case EventConstants.CONTAINER_INVALIDATED: {
        const {containerId} = action;
        this._bulkUpdate((bulkDraft) => {
          this._invalidateStorables([containerId], bulkDraft);
        });
        this.emitChange();
        break;
      }

      case EventConstants.CONTAINER_TAGS_UPDATE_COMPLETED:
      case EventConstants.CONTAINER_UPDATE_COMPLETED: {
        const {container, parentStorable} = action;
        this._bulkUpdate((bulkDraft) => {
          this._processContainer(bulkDraft, container, parentStorable);
        });
        this.emitChange();
        break;
      }

      case EventConstants.CONTAINER_BULK_UPDATE_COMPLETED: {
        const {containers} = action;
        this._bulkUpdate((bulkDraft) => {
          containers.forEach((container: Container) => {
            this._processContainer(bulkDraft, container);
          });
        });
        this.emitChange();
        break;
      }

      case EventConstants.CONTAINER_CONTENT_UPDATE_COMPLETED: {
        const {containerContents, gridId} = action;
        this._bulkUpdate((bulkDraft) => {
          const newContentsByContainerId: {
            [containerId: string]: Array<ContainerContent>;
          } = {};
          const containableConcentrationsByContainerId: {
            [containerId: string]: {[containableId: string]: LegacyMeasurement};
          } = {};

          containerContents.forEach((content: ContainerContent) => {
            const prevContainerContents =
              newContentsByContainerId[content.containerId] ??
              bulkDraft.containers[content.containerId]?.contents ??
              [];
            const newContainerContents = prevContainerContents.map((prevContent) =>
              prevContent.containedItemId === content.containedItemId ? content : prevContent
            );
            newContentsByContainerId[content.containerId] = newContainerContents;

            if (containableConcentrationsByContainerId[content.containerId] === undefined) {
              containableConcentrationsByContainerId[content.containerId] = {
                ...(bulkDraft.storableSummaryExtras.containableConcentrationsByContainerId
                  ? (bulkDraft.storableSummaryExtras.containableConcentrationsByContainerId[
                      content.containerId
                    ] ?? {})
                  : {}),
              };
            }
            containableConcentrationsByContainerId[content.containerId][content.containedItemId] =
              content.concentration;
          });

          Object.keys(newContentsByContainerId).forEach((containerId) => {
            if (bulkDraft.containers[containerId] != null) {
              this._processContainer(bulkDraft, {
                ...bulkDraft.containers[containerId],
                contents: newContentsByContainerId[containerId],
              });
            }
          });

          this._mergeStorableSummaryExtras({containableConcentrationsByContainerId}, bulkDraft);

          if (gridId) {
            this._invalidateStorables([gridId], bulkDraft);
          }
        });
        this.emitChange();
        break;
      }

      case EventConstants.CONTAINERS_TRANSFER_COMPLETED:
      case EventConstants.STORABLES_MOVE_COMPLETED:
      case EventConstants.CONTAINERS_TRANSFER_UNDO_COMPLETED:
      case EventConstants.CONTAINER_CONTENTS_DELETE_COMPLETED: {
        const {invalidatableStorableIds, invalidatableContainedItemIds} = action;
        this._bulkUpdate((bulkDraft) => {
          this._invalidateStorables(invalidatableStorableIds, bulkDraft);
          if (invalidatableContainedItemIds) {
            this._invalidateContainedItemListings(invalidatableContainedItemIds, bulkDraft);
          }
        });
        this.emitChange();
        break;
      }

      case EventConstants.GRID_FETCH_COMPLETED: {
        const {grid, parentStorable} = action;
        this._bulkUpdate((bulkDraft) => {
          this._processGrid(grid, bulkDraft);
          this._processParentStorableSummary(bulkDraft, parentStorable);
        });
        this.emitChange();
        break;
      }

      case EventConstants.GRID_UPDATE_STARTED: {
        const {gridId} = action;
        this._bulkUpdate((bulkDraft) => {
          const newGrid = {...bulkDraft.grids[gridId], isLoading: true};
          bulkDraft.grids[gridId] = newGrid as Grid;
        });
        this.emitChange();
        break;
      }

      case EventConstants.GRID_UPDATE_COMPLETED: {
        const {grid: gridSummary, parentStorable} = action;
        this._bulkUpdate((bulkDraft) => {
          this._processGridSummary(gridSummary, bulkDraft);
          this._processParentStorableSummary(bulkDraft, parentStorable);
        });
        this.emitChange();
        break;
      }

      case EventConstants.GRID_UPDATE_FAILED: {
        const {gridId} = action;
        this._bulkUpdate((bulkDraft) => {
          const newGrid = {...bulkDraft.grids[gridId], isLoading: false};
          bulkDraft.grids[gridId] = newGrid as Grid;
        });
        this.emitChange();
        break;
      }

      case EventConstants.GRID_INVALIDATED: {
        const {gridId} = action;
        this._bulkUpdate((bulkDraft) => {
          this._invalidateStorables([gridId], bulkDraft);
        });
        this.emitChange();
        break;
      }

      case EventConstants.GRID_CLOSE: {
        const {gridId} = action;
        // eslint-disable-next-line no-restricted-syntax
        this.grids = _.omit(this.grids, gridId);
        this.emitChange();
        break;
      }

      case EventConstants.LOCATION_FETCH_COMPLETED: {
        const {location, ancestralLocations} = action;
        this._bulkUpdate((bulkDraft) => {
          bulkDraft.locations[location.id] = location;
          for (const ancestralLocation of ancestralLocations || []) {
            bulkDraft.locationSummaries[ancestralLocation.id] = ancestralLocation;
          }
        });
        this.emitChange();
        break;
      }

      case EventConstants.BATCHES_BULK_FETCH_COMPLETED: {
        const {batchesById} = action;
        this._bulkUpdate((bulkDraft) => {
          this._updateBatches(Object.values(batchesById), bulkDraft);
        });
        this.emitChange();
        break;
      }

      case EventConstants.SPREADSHEET_BULK_TRANSFER_STORABLES_COMPLETED: {
        const {invalidatableStorableIds} = action;
        this._bulkUpdate((bulkDraft) => {
          this._invalidateStorables(invalidatableStorableIds, bulkDraft);
        });
        this.emitChange();
        break;
      }

      case EventConstants.BULK_ARCHIVE_STORABLES_COMPLETED:
      case EventConstants.BULK_UNARCHIVE_STORABLES_COMPLETED: {
        const {itemIds} = action;
        this._bulkUpdate((bulkDraft) => {
          itemIds.forEach((itemId: string) => {
            // The bulk-archive-storables endpoint returns all items that were archived, which are storables
            // and sometimes their contained samples
            this._invalidateContainedSample(itemId);
          });
          this._invalidateStorables(itemIds, bulkDraft);
        });
        this.emitChange();
        break;
      }

      case EventConstants.LABEL_TEMPLATES_FETCH_COMPLETED: {
        this.labelTemplates = action.labelTemplates;
        this.emitChange();
        break;
      }

      case EventConstants.LABEL_TEMPLATE_CREATE_COMPLETED: {
        const {labelTemplate} = action;
        if (!this.labelTemplates) {
          this.labelTemplates = [labelTemplate];
        } else {
          this.labelTemplates = produce(this.labelTemplates, (draftTemplates) => {
            draftTemplates.push(labelTemplate);
          });
        }
        this.emitChange();
        break;
      }

      case EventConstants.LABEL_TEMPLATE_UPDATE_COMPLETED: {
        const {labelTemplate} = action;
        // eslint-disable-next-line no-restricted-syntax
        const index = _.findIndex(this.labelTemplates!, ({id}) => id === labelTemplate.id);
        assert(index > -1, 'Expected labelTemplate to exist in the store.');
        this.labelTemplates = produce(this.labelTemplates!, (draftTemplates) => {
          draftTemplates[index] = labelTemplate;
        });
        this.setSavingState(SavingState.SUCCESS);
        this.emitChange();
        break;
      }

      case EventConstants.LABEL_TEMPLATE_DELETE_COMPLETED: {
        this.labelTemplates = this.labelTemplates!.filter(({id}) => id !== action.labelTemplateId);
        this.emitChange();
        break;
      }

      case EventConstants.PRINTERS_FETCH_COMPLETED: {
        this.printers = action.printers;
        this.emitChange();
        break;
      }

      case EventConstants.PRINTER_CREATE_COMPLETED: {
        const {printer} = action;
        if (!this.printers) {
          this.printers = [printer];
        } else {
          this.printers = produce(this.printers, (draftPrinters) => {
            draftPrinters.push(printer);
          });
        }
        this.emitChange();
        break;
      }

      case EventConstants.PRINTER_UPDATE_COMPLETED: {
        const {printer} = action;
        // eslint-disable-next-line no-restricted-syntax
        const index = _.findIndex(this.printers!, ({id}) => id === printer.id);
        assert(index > -1, 'Expected printer to exist in the store.');
        this.printers = produce(this.printers!, (draftPrinters) => {
          draftPrinters[index] = printer;
        });
        this.setSavingState(SavingState.SUCCESS);
        this.emitChange();
        break;
      }

      case EventConstants.PRINTER_DELETE_COMPLETED: {
        this.printers = this.printers!.filter(({id}) => id !== action.printerId);
        this.emitChange();
        break;
      }

      case EventConstants.LABEL_TEMPLATE_UPDATE_STARTED:
      case EventConstants.PRINTER_UPDATE_STARTED:
        this.setSavingState(SavingState.PENDING);
        this.emitChange();
        break;

      case EventConstants.LABEL_TEMPLATE_UPDATE_FAILED:
      case EventConstants.PRINTER_UPDATE_FAILED:
        this.setSavingState(SavingState.FAILURE);
        this.emitChange();
        break;

      case EventConstants.STORABLE_STORE_RESET_SAVING_STATE:
        this.setSavingState(SavingState.COMPLETED);
        this.emitChange();
        break;

      case EventConstants.CONTAINABLE_CLEAR_CONTAINER_LISTING: {
        const {listingId} = action;
        this.containableContainerListings = this.containableContainerListings.remove(listingId);
        delete this.listingIdToContainableId[listingId];
        this.emitChange();
        break;
      }

      case EventConstants.CONTAINABLE_CLEAR_PLATE_LISTING: {
        const {listingId} = action;
        this.containablePlateListings = this.containablePlateListings.remove(listingId);
        delete this.listingIdToContainableId[listingId];
        this.emitChange();
        break;
      }

      case EventConstants.CONTAINABLE_LIST_CONTAINERS_STARTED: {
        const {listingId, containableId} = action;
        const currentListing = this.containableContainerListings.get(listingId);
        if (currentListing) {
          this.containableContainerListings = this.containableContainerListings.set(
            listingId,
            listingRestart(currentListing)
          );
        } else {
          this.containableContainerListings = this.containableContainerListings.set(
            listingId,
            listingStart({containableId})
          );
        }
        this.listingIdToContainableId[listingId] = containableId;
        this.emitChange();
        break;
      }

      case EventConstants.CONTAINABLE_LIST_CONTAINERS_FAILED: {
        const {listingId} = action;
        const listing = this.containableContainerListings.get(listingId);
        if (listing) {
          this.containableContainerListings.set(listingId, listingFail(listing));
        }
        this.emitChange();
        break;
      }

      case EventConstants.CONTAINABLE_LIST_CONTAINERS_COMPLETED: {
        const {listingId, containers, pagination, storableSummaryExtras} = action;
        this._bulkUpdate((bulkDraft) => {
          const listing = bulkDraft.containableContainerListings.get(listingId);
          if (listing) {
            bulkDraft.containableContainerListings = bulkDraft.containableContainerListings.set(
              listingId,
              listingComplete(
                listing,
                containers.map((container: ContainerSummary) => container.id),
                pagination
              )
            );
          }

          for (const container of containers) {
            this._processStorableSummary(container, bulkDraft);
          }
          if (storableSummaryExtras) {
            this._mergeStorableSummaryExtras(storableSummaryExtras, bulkDraft);
          }
        });
        this.emitChange();
        break;
      }

      case EventConstants.CONTAINABLE_LIST_PLATES_STARTED: {
        const {listingId, containableId} = action;
        this.containablePlateListings = this.containablePlateListings.set(
          listingId,
          listingStart({containableId})
        );
        this.listingIdToContainableId[listingId] = containableId;
        this.emitChange();
        break;
      }

      case EventConstants.CONTAINABLE_LIST_PLATES_FAILED: {
        const {listingId} = action;
        const listing = this.containablePlateListings.get(listingId);
        if (listing) {
          this.containablePlateListings = this.containablePlateListings.set(listingId, listingFail(listing));
        }
        this.emitChange();
        break;
      }

      case EventConstants.CONTAINABLE_LIST_PLATES_COMPLETED: {
        const {listingId, plates, pagination, plateSummaryExtras, storableSummaryExtras} = action;
        this._bulkUpdate((bulkDraft) => {
          const listing = bulkDraft.containablePlateListings.get(listingId);
          if (listing) {
            bulkDraft.containablePlateListings = bulkDraft.containablePlateListings.set(
              listingId,
              listingComplete(
                listing,
                plates.map((plate: GridSummary) => plate.id),
                pagination
              )
            );
          }
          for (const plate of plates) {
            this._processStorableSummary(plate, bulkDraft);
          }
          if (storableSummaryExtras) {
            this._mergeStorableSummaryExtras(storableSummaryExtras, bulkDraft);
          }
          if (plateSummaryExtras) {
            this._mergePlateSummaryExtras(plateSummaryExtras, bulkDraft);
          }
        });
        this.emitChange();
        break;
      }

      case EventConstants.CICO_STATUS_UPDATED: {
        const itemIdToUpdatedStatusInfo: {[itemId: string]: CheckOutStatusInfo} =
          action.itemIdToUpdatedStatusInfo;
        this._bulkUpdate((bulkDraft) => {
          for (const [itemId, updatedStatusInfo] of Object.entries(itemIdToUpdatedStatusInfo)) {
            assertAndContinue(
              APIUtil.isContainer(itemId),
              'Expecting CICO status updates on containers only'
            );
            if (bulkDraft.containers[itemId]) {
              bulkDraft.containers[itemId].checkOutStatusInfo = updatedStatusInfo;
            }
            if (bulkDraft.containerSummaries[itemId]) {
              bulkDraft.containerSummaries[itemId].checkOutStatusInfo = updatedStatusInfo;
            }
          }
        });
        this.emitChange();
        break;
      }

      default:
        break;
    }

    const newStorableAuthState = StorableAuth.SubStore.handleAction(this.storableAuthState, action);
    if (newStorableAuthState !== this.storableAuthState) {
      this.storableAuthState = newStorableAuthState;
      this.emitChange();
    }

    return true;
  }
}

const storableStore = new StorableStore(AppDispatcher);
export default storableStore;
